import React from "react";
import { useTable, usePagination, useGlobalFilter, useFilters, useAsyncDebounce, useRowSelect, useMountedLayoutEffect } from "react-table";
import {matchSorter} from 'match-sorter'
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import NativeSelect from "@mui/material/NativeSelect";
import { makeStyles } from "@mui/styles";
import Checkbox from "@mui/material/Checkbox";
import {Button, Grid, Paper, Box} from "@mui/material";

const useStyles = makeStyles(() => ({
    paper: {
        textAlign: "center",
        overflow: 'auto',
    },

}))

const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef()
        const resolvedRef = ref || defaultRef

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate
        }, [resolvedRef, indeterminate])

        return (
            <>
                <Checkbox ref={resolvedRef} {...rest} />
            </>
        )
    }
)

export default function TableFilterBase({ columns, data, selectedRows, onSelectedRowsChange, search, selectable }) {

    // Define a default UI for filtering
    function GlobalFilter({
                              preGlobalFilteredRows,
                              globalFilter,
                              setGlobalFilter,
                          }) {
        const count = preGlobalFilteredRows.length
        const [value, setValue] = React.useState(globalFilter)
        const onChange = useAsyncDebounce(value => {
            setGlobalFilter(value || undefined)
        }, 200)

        return (
            <span>
        Buscar en:{' '}
                <input
                    value={value || ""}
                    //className={"form-control"}
                    onChange={e => {
                        setValue(e.target.value);
                        onChange(e.target.value);
                    }}
                    placeholder={`${count} registros...`}
                    style={{
                        fontSize: '1.1rem',
                        border: '0',
                    }}
                />
      </span>
        )
    }

    // Define a default UI for filtering
    function DefaultColumnFilter({
                                     column: { filterValue, preFilteredRows, setFilter },
                                 }) {
        const count = preFilteredRows.length

        return (
            <input
                value={filterValue || ''}
                onChange={e => {
                    setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
                }}
                placeholder={`Search ${count} records...`}
            />
        )
    }

    function fuzzyTextFilterFn(rows, id, filterValue) {
        return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
    }

    // Let the table remove the filter if the string is empty
    fuzzyTextFilterFn.autoRemove = val => !val


    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
        }),
        []
    )

    const filterTypes = React.useMemo(
        () => ({
            // Add a new fuzzyTextFilterFn filter type.
            fuzzyText: fuzzyTextFilterFn,
            dateBetween: dateBetweenFilterFn,   /*<- LIKE THIS*/
            // Or, override the default text filter to use
            // "startWith"
            text: (rows, id, filterValue) => {
                return rows.filter(row => {
                    const rowValue = row.values[id]
                    return rowValue !== undefined
                        ? String(rowValue)
                            .toLowerCase()
                            .startsWith(String(filterValue).toLowerCase())
                        : true
                })
            },
        }),
        []
    )

    const props = useTable(
        {
            columns,
            data,
            initialState: {
                selectedRowIds: selectedRows
            },
            defaultColumn, // Be sure to pass the defaultColumn option
            filterTypes,
        },
        useFilters, // useFilters!
        useGlobalFilter,
        usePagination,
        useRowSelect,
        hooks => {

            if (selectable) {
                hooks.visibleColumns.push(columns => [
                    // Let's make a column for selection
                    {
                        id: 'selection',
                        // The header can use the table's getToggleAllRowsSelectedProps method
                        // to render a checkbox
                        Header: ({ getToggleAllPageRowsSelectedProps }) => (
                            <div>
                                <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
                            </div>
                        ),
                        // The cell can use the individual row's getToggleRowSelectedProps method
                        // to the render a checkbox
                        Cell: ({ row }) => (
                            <div>
                                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                            </div>
                        ),
                    },
                    ...columns,
                ])
            }

        }
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        visibleColumns,
        preGlobalFilteredRow,
        setGlobalFilter,
        page,
        rows,
        canPreviousPage,
        canNextPage,
        pageOptions,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize, globalFilter, selectedRowIds },
    } = props;

    useMountedLayoutEffect(() => {
        onSelectedRowsChange && onSelectedRowsChange(selectedRowIds)
    }, [onSelectedRowsChange, selectedRowIds]);

    React.useEffect(() => {
    }, [globalFilter]);

    React.useEffect(() => {
        setPageSize(100);
    },[])

    const classes = useStyles();


    function dateBetweenFilterFn(rows, id, filterValues) {
        let sd = filterValues[0] ? new Date(filterValues[0]) : undefined
        let ed = filterValues[1] ? new Date(filterValues[1]) : undefined

        if (ed || sd) {
            return rows.filter(r => {
                var time = new Date(r.values[id])

                if (ed && sd) {
                    return (time >= sd && time <= ed)
                } else if (sd){
                    return (time >= sd)
                } else if (ed){
                    return (time <= ed)
                }
            })
        } else {
            return rows
        }
    }

    // We don't want to render all of the rows for this example, so cap
    // it for this use case
    const firstPageRows = rows.slice(0, 10)

    return (
        <>
            {search && (
                <Box m={2}>
                    <TextField
                        variant="outlined"
                        placeholder="Buscador"
                        label="Buscador"
                        type="search"
                        fullWidth
                        value={globalFilter || ""}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                </Box>
            )}
            <Box m={0} style={{overflow:"auto"}}>
                <Table {...getTableProps()} >
                    <TableHead style={{ background: "#f1f2f6" }}>
                        {headerGroups.map((headerGroup, key) => (
                            <TableRow {...headerGroup.getHeaderGroupProps()} key={key}>
                                {headerGroup.headers.map((column, key) => (
                                    <TableCell {...column.getHeaderProps()} key={key} className="text-center">
                                        {column.render("Header")}
                                        {/* Render the columns filter UI */}
                                        <div>{column.canFilter ? column.render('Filter') : null}</div>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        <tr>
                            <th
                                colSpan={visibleColumns.length}
                                style={{
                                    textAlign: 'left',
                                }}
                            >
                                {/* <GlobalFilter
                preGlobalFilteredRows={preGlobalFilteredRows}
                globalFilter={state.globalFilter}
                setGlobalFilter={setGlobalFilter}
              /> */}
                            </th>
                        </tr>
                    </TableHead>
                    <TableBody {...getTableBodyProps()}>
                        {page.map((row, key) => {
                            prepareRow(row);
                            return (
                                <TableRow {...row.getRowProps()} key={key}>
                                    {row.cells.map((cell, key) => {
                                        return (
                                            <TableCell {...cell.getCellProps()} key={key}>
                                                {cell.render("Cell")}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
            <Box m={2}>
                <Grid container spacing={3}>
                    <Grid item xs={2} md={2} lg={2}>
                        <NativeSelect
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                            }}
                        >
                            {[100, 200, 500, 1000].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    Mostrar {pageSize}
                                </option>
                            ))}
                        </NativeSelect>
                    </Grid>
                    <Grid item xs={8} md={8} lg={8}>
                        <Paper className={classes.paper} elevation={0}>
                            {canPreviousPage ? (
                                <Button onClick={() => previousPage()}>
                  <span role="presentation">
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        role="presentation"
                    >
                      <path
                          fill="currentColor"
                          fillRule="evenodd"
                          d="M9.005 10.995l4.593-4.593a.99.99 0 111.4 1.4l-3.9 3.9 3.9 3.9a.99.99 0 01-1.4 1.4L9.005 12.41a1 1 0 010-1.414z"
                      ></path>
                    </svg>
                  </span>
                                </Button>
                            ) : (
                                <Button disabled>
                  <span role="presentation">
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        role="presentation"
                    >
                      <path
                          fill="currentColor"
                          fillRule="evenodd"
                          d="M9.005 10.995l4.593-4.593a.99.99 0 111.4 1.4l-3.9 3.9 3.9 3.9a.99.99 0 01-1.4 1.4L9.005 12.41a1 1 0 010-1.414z"
                      ></path>
                    </svg>
                  </span>
                                </Button>
                            )}

                            {canNextPage ? (
                                <Button onClick={() => nextPage()} disabled={!canNextPage}>
                  <span role="presentation">
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        role="presentation"
                    >
                      <path
                          fill="currentColor"
                          fillRule="evenodd"
                          d="M14.995 10.995a1 1 0 010 1.414l-4.593 4.593a.99.99 0 01-1.4-1.4l3.9-3.9-3.9-3.9a.99.99 0 011.4-1.4l4.593 4.593z"
                      ></path>
                    </svg>
                  </span>
                                </Button>
                            ) : (
                                <Button disabled>
                  <span role="presentation">
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        role="presentation"
                    >
                      <path
                          fill="currentColor"
                          fillRule="evenodd"
                          d="M14.995 10.995a1 1 0 010 1.414l-4.593 4.593a.99.99 0 01-1.4-1.4l3.9-3.9-3.9-3.9a.99.99 0 011.4-1.4l4.593 4.593z"
                      ></path>
                    </svg>
                  </span>
                                </Button>
                            )}
                        </Paper>
                    </Grid>
                    {/* <Grid item xs={2} md={2} lg={2}>
             Total horas: {totalHoras}
            <br/>
            Total precio: {totalPrecio}
          </Grid> */}
                    <Grid item xs={2} md={2} lg={2}>
                        Página <strong>{pageIndex + 1}</strong> de{" "}
                        <strong> {pageOptions.length} </strong>
                    </Grid>
                </Grid>
            </Box>
        </>
    )
}
